import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import useSWR from "swr";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  ListSubheader,
  TablePagination,
  Tooltip,
  Switch,
  useTheme,
  Snackbar,
  CircularProgress
  // Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ActiveFiltersBar from "components/ActiveFilterBar";
import ClearIcon from '@mui/icons-material/Clear';
import { format, parse, isValid, addDays} from 'date-fns';
import { PermissionWrapper } from "components/PermissionComponents";
import { PERMISSIONS } from "contexts/PermissionContext";
import { useNavigate } from "react-router-dom";
import MainCard from "components/MainCard";
import DateRangePickerWrapper from "components/DateRangePickerWrapper";
import ExportDialog from "components/ExportDialog";
import ImportTransactionsDialog from "components/ImportTransactionsDialog";
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ListViewTransactions from "./ListTransactionView";
import KanbanTransactions from "./kanbantransactionview";
// import PendingCommunication from "./PendingCommunicationList";
import useSelection from "hooks/useSelection";
import useApi from "hooks/useApi";
import useClientAutoSelector from "hooks/useClientAutoSelector";
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import TableRowsIcon from "@mui/icons-material/TableRows";
import ClientFilter from "components/ClientFilter";
import DateRangeIcon from '@mui/icons-material/DateRange';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import useFirebase from "hooks/useFirebase";
import usePendingCommunicationRealtime from "hooks/usePendingCommunicationRealtime";
import { alpha } from '@mui/material/styles';
import { limitToLast, onChildAdded, query, ref } from 'firebase/database';
import ActiveFilterPill from "components/ActiveFilterPill";
type TransactionRefreshEvent = {
  action: "create" | "update";
  teamId: string;
  teamUid?: string;
  clientId?: string;
  clientUid?: string;
  keyId?: string;
  keyUid?: string;
  source: string;
  occurredAt: string;
  meta?: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isTransactionRefreshEvent = (value: unknown): value is TransactionRefreshEvent => {
  if (!isRecord(value)) return false;
  if (value.action !== 'create' && value.action !== 'update') return false;
  if (typeof value.teamId !== 'string' || value.teamId.trim().length === 0) return false;
  if (typeof value.source !== 'string' || value.source.trim().length === 0) return false;
  if (typeof value.occurredAt !== 'string' || value.occurredAt.trim().length === 0) return false;
  if (value.teamUid !== undefined && typeof value.teamUid !== 'string') return false;
  if (value.clientId !== undefined && typeof value.clientId !== 'string') return false;
  if (value.clientUid !== undefined && typeof value.clientUid !== 'string') return false;
  if (value.keyId !== undefined && typeof value.keyId !== 'string') return false;
  if (value.keyUid !== undefined && typeof value.keyUid !== 'string') return false;
  if (value.meta !== undefined && !isRecord(value.meta)) return false;
  return true;
};

// Which form fields the card-view "Fields" search can offer. TEXT and NUMBER
// only: every other input component stores its answer as objects or files
// under data[].value.value (FILE/CAMERA/ESIGN/RESOURCE) or as a non-string
// type the server-side value match cannot reach (DATE/SWITCH), so offering
// them would only ever return an empty board. Module scope so the array is
// not rebuilt on every render.
const SEARCHABLE_INPUT_COMPONENTS = ["TEXT", "NUMBER"];

const NewDashboard: React.FC = () => {
  const {
    getTeamByClient,
    transactionOrder,
    transactionKanban,
    getTeamDetailsByUid,
    getFlowByTeamUid,
    getNodeStatus,
    showToast,
    // getAllPendingCommunication,
    getConsolidatedHxH, 
  } = useApi();
  const { database, firebaseUid } = useFirebase();
  const { setSelectedClient, selectedTeam, setSelectedTeam, selectedClient, clientOptions, dateRange, setDateRange } =
    useSelection();
  const navigate = useNavigate();

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  useClientAutoSelector();

  type ViewMode = "table" | "card";
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce delay
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState<any>(false);
  const [page, setPage] = useState(0);
  const [transactionData, setTransactionData] = useState<any>({});
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [teamDetails, setTeamDetails] = useState<any>(null);
  const [teamLoader, setTeamLoader] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const pendingCommunicationRef = useRef<{ refresh: (showLoader?: boolean) => Promise<boolean> }>(null);
  const realtimeRefreshInFlightRef = useRef(false);
  const realtimeRefreshQueuedRef = useRef(false);
  const searchControllerRef = useRef<AbortController | null>(null);
  // Bumped every time the selected team changes. The two team-scoped fetches
  // below (team details, team flow) capture the generation at call time;
  // stale responses that resolve AFTER the user has switched teams are
  // discarded. Reporting status/node status is handled separately by its own
  // useSWR hook (nodeStatusKey), which doesn't need this — SWR discards
  // stale responses itself once the key changes.
  const teamFetchGenRef = useRef(0);
  // Always holds the latest getTransactions — lets refreshTransactionsFromRealtime stay stable
  // (useCallback) without closing over a stale copy of getTransactions.
  const getTransactionsRef = useRef<(searchValue?: string, showLoader?: boolean) => Promise<boolean>>(
    () => Promise.resolve(false)
  );
  const wasEditModalOpenRef = useRef(false);
  const transactionRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transactionListenerStartedAtRef = useRef<number>(0);
  const pendingCommRefreshInFlightRef = useRef(false);
  const pendingCommRefreshQueuedRef = useRef(false);
  const { notification, acknowledgeAndClear, dismissAndClear } = usePendingCommunicationRealtime({
    database,
    // Gate on the signed-in Firebase uid: never subscribe before auth resolves
    // (the rules would deny it) and key the path off the authenticated uid.
    recipientUid: firebaseUid,
    flushStrategy: 'immediate',
    staleWindowMs: 10 * 60 * 1000,
    removeHandledFromDb: true,
    showSnackbar: false
  });
  const today = new Date();
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(today.getDate() - 7);
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  const theme=useTheme();
  type KanbanViewMode = 'compact' | 'full';
  const [kanbanViewMode, setKanbanViewMode] = useState<KanbanViewMode>('compact');
  const [isKanbanMaximized, setIsKanbanMaximized] = useState(false);

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("dashboardFilters");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (err) {
        console.error("Failed to parse saved filters:", err);
      }
    }
    return {
      selectedClient: "",
      selectedTeamTable: "all",
      selectedTeamCard: "",
      dateType: "createdDate",
      reportingStatus: "",
      status: "",
      dateRange: "",
      sortByKey: false
    };
  });

  const [hxhIframeUrl, setHxhIframeUrl] = useState<string | null>(null);
  const [hxhLoading, setHxhLoading] = useState(false);
  const [hxhActive, setHxhActive] = useState(false);

  // Converts our dd/MM/yyyy date strings to the yyyy-MM-dd format Quikstep expects.
  const toIsoDate = (str?: string): string | undefined => {
    if (!str) return undefined;
    const parsed = parse(str, 'dd/MM/yyyy', new Date());
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined;
  };

  const handleLoadConsolidatedHxH = useCallback(async () => {
    setHxhLoading(true);
    try {
      const dateFrom = toIsoDate(dateRange.startDate);
      const dateTo = toIsoDate(dateRange.endDate);
      const process = filters.selectedTeamTable && filters.selectedTeamTable !== 'all'
        ? teamOptions.find((t: any) => t.uid === filters.selectedTeamTable)?.name
        : undefined;

      const res = await getConsolidatedHxH({ dateFrom, dateTo, process });
      if (res?.success) {
        setHxhIframeUrl(res.data.iframeUrl);
      } else {
        showToast('Failed to load HxH view', 'error');
      }
    } catch (error) {
      console.error('Error loading Consolidated HxH:', error);
      showToast('Failed to load HxH view', 'error');
    } finally {
      setHxhLoading(false);
    }
  }, [dateRange, filters.selectedTeamTable, teamOptions]);

  const handleToggleConsolidatedHxH = () => {
    setHxhActive((prev) => {
      const next = !prev;
      if (!next) {
        setHxhIframeUrl(null); // clear so re-opening always fetches a fresh (unexpired) token
      }
      return next;
    });
  };

  useEffect(() => {
    if (hxhActive) {
      void handleLoadConsolidatedHxH();
    }
  }, [hxhActive, handleLoadConsolidatedHxH]);

  const isDcdClient = clientOptions
    .find((c: any) => c.uid === selectedClient)
    ?.name?.trim()
    ?.toLowerCase() === 'crane worldwide';

  // const handlePendingCommunicationRefresh = () => {
  //   if (pendingCommunicationRef.current) {
  //     pendingCommunicationRef.current.refresh();
  //   }
  // };

  const handleRealtimeRefreshNow = async () => {
    const didRefresh = await pendingCommunicationRef.current?.refresh(true);
    if (didRefresh) {
      await acknowledgeAndClear();
    }
  };

  const handleRealtimeDismiss = async () => {
    await dismissAndClear();
  };

  const clearTransactionRefreshTimer = () => {
    if (!transactionRefreshTimerRef.current) return;
    clearTimeout(transactionRefreshTimerRef.current);
    transactionRefreshTimerRef.current = null;
  };

  // Reporting-status config for the selected team. SWR's cache lives outside
  // the component, so — like the kanban fetch — it survives the unmount that
  // happens when navigating to a transaction's details page and back,
  // showing the known-good status map instantly instead of the board
  // dumping every card into "No Status" while a fresh fetch is in flight.
  const nodeStatusKey = (filters.selectedTeamTable && filters.selectedTeamTable !== 'all')
    ? ['node-status', filters.selectedTeamTable]
    : null;
  const { data: nodeStatusResponse } = useSWR(
    nodeStatusKey,
    () => getNodeStatus(filters.selectedTeamTable),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );
  const nodeStatus = (nodeStatusResponse?.success && Array.isArray(nodeStatusResponse.data))
    ? nodeStatusResponse.data
    : null;
  const keyStatusMap = nodeStatus
    ? nodeStatus.reduce((acc: Record<string, string>, status: any) => {
        acc[status._id] = status.name;
        return acc;
      }, {})
    : null;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [fieldOptions, setFieldOptions] = useState<any[]>([]);
  const [openRange, setOpenRange] = useState(false);
  const [showPriorityOnly, setShowPriorityOnly] = useState(false);

  // What the search box searches. "key" is the original behaviour (fuzzy
  // transaction key). "field" searches the answer recorded for one form
  // field of the selected process, resolved server-side. Card view only —
  // the list endpoint has no field-search pre-pass and simply ignores these,
  // so the picker is hidden rather than reset outside card view.
  type SearchMode = "key" | "field";

  // Persisted like dashboardFilters/viewMode — the page unmounts on every
  // trip to a transaction's details route, so plain useState would reset the
  // picker back to "Txn Key" each time. The saved process uid is part of the
  // record: field uids are per-process, so a pick only restores when the
  // restored process is the one it was made against.
  const SEARCH_TARGET_KEY = "txnSearchTarget";
  const readSavedSearchTarget = (): { mode?: string; field?: string; teamUid?: string } | null => {
    try {
      const raw = localStorage.getItem(SEARCH_TARGET_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (err) {
      console.error("Failed to parse saved search target:", err);
      return null;
    }
  };

  const [searchMode, setSearchMode] = useState<SearchMode>(() => (
    readSavedSearchTarget()?.mode === "field" ? "field" : "key"
  ));
  const [searchField, setSearchField] = useState<string>(() => {
    const saved = readSavedSearchTarget();
    return saved && typeof saved.field === "string" && saved.teamUid === filters.selectedTeamTable
      ? saved.field
      : "";
  });

  // Always write through both values together — a half-written record would
  // restore "field" mode with no field, which reads as a broken filter.
  const persistSearchTarget = (mode: SearchMode, field: string) => {
    try {
      localStorage.setItem(SEARCH_TARGET_KEY, JSON.stringify({ mode, field, teamUid: filters.selectedTeamTable }));
    } catch (err) {
      // Quota or private mode — the picker still works for this session.
      console.error("Failed to save search target:", err);
    }
  };

  const applySearchMode = (mode: SearchMode) => {
    const nextField = mode === "key" ? "" : searchField;
    setSearchMode(mode);
    setSearchField(nextField);
    persistSearchTarget(mode, nextField);
  };

  const applySearchField = (field: string) => {
    setSearchField(field);
    persistSearchTarget(searchMode, field);
  };

  // The searchable fields of the selected process, flattened out of the flow
  // steps already fetched into fieldOptions — no extra request. Deduped by
  // uid because a dynamic field can be re-declared on more than one step.
  const searchableFields = useMemo(() => {
    const seen = new Set<string>();
    const out: { uid: string; question: string; step: string }[] = [];
    (Array.isArray(fieldOptions) ? fieldOptions : []).forEach((step: any) => {
      (step?.component?.fields || []).forEach((field: any) => {
        if (!field?.uid || seen.has(field.uid)) return;
        if (!SEARCHABLE_INPUT_COMPONENTS.includes(field.inputComponent)) return;
        seen.add(field.uid);
        out.push({ uid: field.uid, question: field.question || field.uid, step: step.title || "" });
      });
    });
    return out;
  }, [fieldOptions]);

  // Whether a specific process is active. Field search is defined per process
  // — the field uids, and the key field's own label, both come off its flow —
  // so with "All" selected there is nothing to target and the pickers are off.
  const hasProcess = Boolean(filters.selectedTeamTable) && filters.selectedTeamTable !== 'all';

  // "Txn Key" is the generic name for whatever field the process marks as its
  // auth key, so show that field's own label once we know it. Falls back to
  // "Txn Key" for "All", for a flow still loading, or for a process that marks
  // no field — the search itself is unchanged either way.
  const authKeyFieldLabel = useMemo(() => {
    for (const step of Array.isArray(fieldOptions) ? fieldOptions : []) {
      for (const field of step?.component?.fields || []) {
        if (field?.settings?.isAuthkey === true && field?.question) return String(field.question);
      }
    }
    return "";
  }, [fieldOptions]);

  const keySearchLabel = hasProcess && authKeyFieldLabel ? authKeyFieldLabel : "Transaction number";

  // Typed into the field picker's own search box. Lives here rather than in the
  // Select so closing the menu can clear it.
  const [fieldFilter, setFieldFilter] = useState("");

  const visibleSearchableFields = useMemo(() => {
    const q = fieldFilter.trim().toLowerCase();
    if (!q) return searchableFields;
    return searchableFields.filter((f) => `${f.question} ${f.step}`.toLowerCase().includes(q));
  }, [searchableFields, fieldFilter]);

  // Field mode cannot resolve without a process, and leaving it set would blank
  // the search term (see effectiveSearch below) while the disabled picker still
  // reads "Txn Key" — a search box that silently does nothing. Fall back to key.
  useEffect(() => {
    if (hasProcess || searchMode === "key") return;
    setSearchMode("key");
    setSearchField("");
    persistSearchTarget("key", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcess, searchMode]);

  // A restored pick can name a field that is no longer offered — saved
  // before the TEXT/NUMBER filter existed, or removed from the process
  // since. The Select would render blank while the board stayed filtered on
  // it, so drop it once the field list for this process is known.
  useEffect(() => {
    if (!searchField || searchableFields.length === 0) return;
    if (searchableFields.some((f) => f.uid === searchField)) return;
    setSearchField("");
    persistSearchTarget(searchMode, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchableFields, searchField, searchMode]);

  // Field mode with nothing picked yet is a half-entered filter, not an
  // empty-result search: blank the term so the board stays unfiltered until
  // a field is chosen, instead of silently running a key search on it.
  const isFieldSearchReady = searchMode === "field" && searchField !== "";
  const effectiveSearch = searchMode === "field" && !isFieldSearchReady ? "" : debouncedSearch;

  const toggle = () => setOpenRange(!openRange);

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters((prev: any) => {
      let updated = { ...prev, [key]: value };
      if (key === "selectedTeamTable" || key === "selectedTeamCard") {
        updated = {
          ...updated,
          reportingStatus: "",
          status: ""
        };
      }
      localStorage.setItem("dashboardFilters", JSON.stringify(updated));
      return updated;
    });
  };

  const handleDateRangeChange = (range: any) => {
    const formatDate = (date: any) => {
      if (typeof date === 'string') {
        return date;
      }
      if (date instanceof Date && isValid(date)) {
        return format(date, 'dd/MM/yyyy');
      }
      return date;
    };

    const formattedRange = {
      startDate: formatDate(range.startDate),
      endDate: formatDate(range.endDate)
    };

    setDateRange(formattedRange);
  };

  useEffect(() => {
    if (!notification.lastEventAt) return;
    if (!selectedClient || !filters.selectedTeamTable) return;

    let cancelled = false;

    const run = async () => {
      if (pendingCommRefreshInFlightRef.current) {
        pendingCommRefreshQueuedRef.current = true;
        return;
      }

      pendingCommRefreshInFlightRef.current = true;
      try {
        // Coalesce bursts: if more events arrive mid-refresh, run again immediately.
        // This avoids any artificial wait while still preventing overlapping API calls.
        do {
          pendingCommRefreshQueuedRef.current = false;

          const didRefresh = await pendingCommunicationRef.current?.refresh(false);
          if (cancelled) return;

          if (didRefresh) {
            await acknowledgeAndClear();
          } else {
            // If refresh failed, do not ack; leave events to retry on next trigger.
            break;
          }
        } while (pendingCommRefreshQueuedRef.current && !cancelled);
      } finally {
        pendingCommRefreshInFlightRef.current = false;
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [acknowledgeAndClear, filters.selectedTeamTable, notification.lastEventAt, selectedClient]);

  // Initial cards per kanban column; "Load more" fetches the same amount again.
  const KANBAN_COLUMN_SIZE = 50;

  const buildKanbanPayload = (searchValue: string) => ({
    search: searchValue,
    // Omitted by every other caller of /authkey/kanban; the endpoint falls
    // back to the original key search when searchMode is absent or "key".
    searchMode,
    searchField: isFieldSearchReady ? searchField : "",
    from: dateRange.startDate,
    to: dateRange.endDate,
    clients: selectedClient ? [selectedClient] : [],
    teams: [filters.selectedTeamTable],
    reportingStatus: filters.reportingStatus,
    dateType: filters.dateType,
    status: filters.status,
    users: [],
    eventTypes: [],
    eventStatus: [],
    columnSize: KANBAN_COLUMN_SIZE,
    priorityOnly: showPriorityOnly,
    sortByKey: filters.sortByKey,
  });

  // Kanban's own cache, independent of the table-view transactionData/loading
  // state below. SWR's cache lives outside the component, so it survives the
  // unmount that happens when navigating to a transaction's details page
  // (transactions/details/... is a sibling route, not nested) — coming back
  // shows the last board instantly instead of refetching from a blank state.
  const kanbanKey = (viewMode === "card" && selectedClient && filters.selectedTeamTable && filters.selectedTeamTable !== 'all')
    ? ['kanban', selectedClient, filters.selectedTeamTable, dateRange.startDate, dateRange.endDate, effectiveSearch, searchMode, isFieldSearchReady ? searchField : '', filters.reportingStatus, filters.dateType, filters.status, showPriorityOnly, filters.sortByKey]
    : null;

  const { data: kanbanResponse, isLoading: kanbanIsLoading, mutate: mutateKanban } = useSWR(
    kanbanKey,
    () => transactionKanban(buildKanbanPayload(effectiveSearch)),
    // keepPreviousData: switching search target or picking a field changes
    // the key, and without this the board blanks to a spinner between the
    // two states.
    { revalidateOnFocus: false, revalidateOnReconnect: false, keepPreviousData: true },
  );
  const kanbanData = kanbanResponse?.success ? kanbanResponse.data : {};

  // Per-column "Load more": fetches the next page for a single kanban column.
  // Returns { keyStatus, count, rows } or null on failure.
  const loadMoreKanbanColumn = async (statusKey: string, columnPage: number): Promise<any | null> => {
    try {
      const payload = { ...buildKanbanPayload(effectiveSearch), column: statusKey, columnPage };
      const { success, data } = await transactionKanban(payload);
      return success ? data?.columns?.[0] ?? null : null;
    } catch (error) {
      console.error('Error loading more kanban cards:', error);
      return null;
    }
  };

  const getTransactions = async (searchValue = debouncedSearch, showLoader = true): Promise<boolean> => {
    // Create a controller for this request.
    // Only user-initiated loads (showLoader=true) abort the previous in-flight user search;
    // background silent refreshes (showLoader=false) do not interrupt user searches.
    const controller = new AbortController();
    if (showLoader) {
      searchControllerRef.current?.abort();
      searchControllerRef.current = controller;
    }

    try {
      // Kanban only renders for a specific process — skip the fetch and let
      // the board show its "select a process" empty state.
      if (viewMode === "card" && (!selectedClient || !filters.selectedTeamTable || filters.selectedTeamTable === 'all')) {
        setTransactionData({});
        return false;
      }

      if (showLoader) {
        setLoading(true);
        setTransactionData({});
      }
      const payload = viewMode === "card"
        ? buildKanbanPayload(effectiveSearch)
        : {
            page: page + 1,
            size: rowsPerPage,
            search: searchValue,
            from: dateRange.startDate,
            to: dateRange.endDate,
            clients: selectedClient ? [selectedClient] : [],
            teams: [filters.selectedTeamTable],
            reportingStatus: filters.reportingStatus,
            dateType: filters.dateType,
            status: filters.status,
            users: [],
            keyword: "",
            eventTypes: [],
            eventStatus: [],
          };

      const data = viewMode === "card"
        ? await transactionKanban(payload, { signal: controller.signal })
        : await transactionOrder(payload, { signal: controller.signal });

      // Discard result if this request was superseded
      if (controller.signal.aborted) return false;

      const { success, data: transData } = data;

      if (success) {
        setTransactionData(transData);
        return true;
      }
      return false;
    } catch (error: any) {
      // Silently discard aborted/cancelled requests — do not update state or log an error
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return false;
      }
      console.error('Error fetching transactions:', error);
      return false;
    } finally {
      // Only clear the loading indicator if this is still the active user-initiated request.
      // Stale aborted requests must not clear loading while a newer request is in-flight.
      if (showLoader && searchControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  // Keep the ref pointing at the latest getTransactions on every render.
  getTransactionsRef.current = getTransactions;

  const handleRefresh = async () => {
    if (!selectedClient || !selectedTeam) {
      showToast('Please select a client and process to refresh transactions', 'warning');
      return;
    }

    try {
      if (viewMode === "card") {
        await mutateKanban();
      } else {
        await getTransactions(debouncedSearch);
      }
      showToast('Transactions refreshed successfully', 'success');
    } catch (error) {
      showToast('Failed to refresh transactions', 'error');
    }
  };

  const FILTER_RESET = {
  selectedClient:    '',
  selectedTeamTable: 'all',
  selectedTeamCard:  '',
  dateType:          'createdDate',
  reportingStatus:   '',
  status:            '',
  sortByKey:         false,
};


const handleRemoveFilter = (key: string) => {
  if (key === 'dateRange') {
    const start = addDays(new Date(), -20);
    const end   = addDays(new Date(),  20);
    setDateRange({
      startDate: format(start, 'dd/MM/yyyy'),
      endDate:   format(end,   'dd/MM/yyyy'),
    });
    return;
  }
  if (key === 'showPriorityOnly') {      
    setShowPriorityOnly(false);
    return;
  }
  handleFilterChange(key as keyof typeof filters, FILTER_RESET[key as keyof typeof FILTER_RESET] ?? '');
};

const handleClearAllFilters = () => {
  const start = addDays(new Date(), -20);
  const end   = addDays(new Date(),  20);
  setDateRange({
    startDate: format(start, 'dd/MM/yyyy'),
    endDate:   format(end,   'dd/MM/yyyy'),
  });
  setFilters(FILTER_RESET);
  setShowPriorityOnly(false);
  setShowAdvancedFilters(false);   // ← add this
  localStorage.setItem('dashboardFilters', JSON.stringify(FILTER_RESET));
};
  
  const refreshTransactionsFromRealtime = useCallback(async () => {
    if (isEditModalOpen) {
      realtimeRefreshQueuedRef.current = true;
      return;
    }

    if (realtimeRefreshInFlightRef.current) {
      realtimeRefreshQueuedRef.current = true;
      return;
    }

    realtimeRefreshInFlightRef.current = true;
    try {
      do {
        if (isEditModalOpen) {
          realtimeRefreshQueuedRef.current = true;
          break;
        }
        realtimeRefreshQueuedRef.current = false;
        if (viewMode === "card") {
          await mutateKanban();
        } else {
          await getTransactionsRef.current(search, false);
        }
      } while (realtimeRefreshQueuedRef.current && !isEditModalOpen);
    } finally {
      realtimeRefreshInFlightRef.current = false;
    }
  }, [isEditModalOpen, search, viewMode, mutateKanban]);

  useEffect(() => {
    // Kanban fetch is owned by the kanbanKey-driven useSWR hook above — it
    // reacts to the same inputs on its own, so calling getTransactions here
    // too would be a duplicate `/authkey/kanban` request.
    if (viewMode === "card") return;
    getTransactions(debouncedSearch);
    // `selectedTeam` intentionally excluded — the request payload uses
    // `filters.selectedTeamTable`, not `selectedTeam`, which isn't read
    // anywhere in getTransactions. Including it caused a duplicate fetch
    // (and a canceled `authkey/keys` request) whenever fetchTeams reset
    // `selectedTeam` to 'all' after the client's team list loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dateRange, selectedClient, debouncedSearch, filters, viewMode]);

  useEffect(() => {
    const selectedTeamTable = filters.selectedTeamTable;
    console.log("Setting up realtime listener for team:", selectedTeamTable);
    clearTransactionRefreshTimer();
    realtimeRefreshQueuedRef.current = false;

    // Wait for Firebase Auth: transaction-refresh reads require auth != null, so
    // attaching before signInWithCustomToken resolves would be denied.
    if (!database || !firebaseUid || !selectedClient) {
      return;
    }

    const isSpecificTeam = Boolean(selectedTeamTable && selectedTeamTable !== 'all');
    const listenerPath = isSpecificTeam
      ? `transaction-refresh/team/${selectedTeamTable}`
      : `transaction-refresh/client/${selectedClient}`;

    if (!listenerPath) {
      return;
    }

    transactionListenerStartedAtRef.current = Date.now();

    // Bound the initial sync: the transaction-refresh node accumulates events,
    // and onChildAdded would otherwise download all of them on every attach.
    const streamRef = query(ref(database, listenerPath), limitToLast(20));
    const unsubscribe = onChildAdded(streamRef, (snapshot) => {
      const payload = snapshot.val() as unknown;
      if (!isTransactionRefreshEvent(payload)) {
        return;
      }

      const occurredAtTs = Date.parse(payload.occurredAt);
      if (Number.isNaN(occurredAtTs) || occurredAtTs < transactionListenerStartedAtRef.current) {
        return;
      }

      if (isSpecificTeam) {
        const teamMatches = [payload.teamId, payload.teamUid]
          .filter((value): value is string => Boolean(value))
          .includes(selectedTeamTable);
        if (!teamMatches) {
          return;
        }
      } else {
        const clientCandidates = [payload.clientId, payload.clientUid]
          .filter((value): value is string => Boolean(value));
        const clientMatches = clientCandidates.includes(selectedClient);
        if (clientCandidates.length > 0 && !clientMatches) {
          return;
        }
      }

      if (realtimeRefreshInFlightRef.current) {
        realtimeRefreshQueuedRef.current = true;
        return;
      }

      if (isEditModalOpen) {
        realtimeRefreshQueuedRef.current = true;
        return;
      }

      realtimeRefreshQueuedRef.current = true;
      void refreshTransactionsFromRealtime();
    });

    return () => {
      unsubscribe();
      clearTransactionRefreshTimer();
      realtimeRefreshQueuedRef.current = false;
    };
  }, [database, firebaseUid, filters.selectedTeamTable, selectedClient, page, rowsPerPage, search, dateRange, filters.reportingStatus, filters.dateType, filters.status, viewMode, isEditModalOpen, refreshTransactionsFromRealtime]);

  useEffect(() => {
    if (wasEditModalOpenRef.current && !isEditModalOpen) {
      void refreshTransactionsFromRealtime();
    }
    wasEditModalOpenRef.current = isEditModalOpen;
  }, [isEditModalOpen, refreshTransactionsFromRealtime]);

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: ViewMode | null
  ) => {
    if (newView !== null) {
      // Kanban owns its own loading/data via the kanbanKey-driven SWR hook —
      // only reset the table-view state when switching into table view.
      if (newView !== "card") {
        setLoading(true)
        setTransactionData([]);
        // The search target is deliberately NOT reset here. The list
        // endpoint ignores it (the table always key-searches) and the
        // picker is hidden outside card view, so resetting would only
        // throw away the preference on a there-and-back view toggle.
      }
      setViewMode(newView);
      localStorage.setItem("viewMode", newView);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setDebouncedSearch(search);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    const savedView = localStorage.getItem("viewMode") as ViewMode | null;
    if (savedView) {
      setViewMode(savedView);
    }
  }, []);

 // Add near the other refs
const teamFlowCacheRef = useRef<Record<string, any[]>>({});

const fetchTeamData = async (teamUid: string, gen: number) => {
  try {
    // Return cached result immediately if available
    if (teamFlowCacheRef.current[teamUid]) {
      if (gen !== teamFetchGenRef.current) return;
      setFieldOptions(teamFlowCacheRef.current[teamUid]);
      return;
    }

    const flowResp = await getFlowByTeamUid(teamUid);
    if (gen !== teamFetchGenRef.current) return;

    if (flowResp?.data?.steps) {
      teamFlowCacheRef.current[teamUid] = flowResp.data.steps; // cache it
      setFieldOptions(flowResp.data.steps);
    }
  } catch (error) {
    console.error('Error fetching team data:', error);
  }
};

  // Guards against the effect below firing its fetch trio twice for the
  // same team (observed in the network panel as duplicate team/flow/node-status
  // calls) when filters.selectedTeamTable settles across more than one render
  // during mount/restore. Kept before the gen bump so a legitimate genuine
  // team switch is never skipped — only an exact repeat of the last team we
  // already kicked a fetch off for.
  const lastFetchedTeamUidRef = useRef<string | null>(null);

  useEffect(() => {
    const teamUid = filters.selectedTeamTable;
    if (teamUid === lastFetchedTeamUidRef.current) {
      return;
    }
    const previousTeamUid = lastFetchedTeamUidRef.current;
    lastFetchedTeamUidRef.current = teamUid;

    // Field uids are per-process — a pick from the previous process would
    // match nothing here. Skipped on the mount pass (previous === null),
    // which is not a process change and would otherwise clear the pick
    // restored from localStorage a moment earlier.
    if (previousTeamUid !== null) {
      setSearchField("");
      persistSearchTarget(searchMode, "");
    }

    // Bump the generation token so any in-flight team-scoped fetch from a
    // previous team is ignored when it resolves. Capture the current
    // generation — each fetch below compares against teamFetchGenRef.current
    // before touching state.
    const myGen = ++teamFetchGenRef.current;

    void Promise.all([
      getTeamDetails(teamUid, myGen),
      fetchTeamData(teamUid, myGen),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.selectedTeamTable])

  const getTeamDetails = async (teamUid: string, gen: number) => {
    try {
      const res = await getTeamDetailsByUid(teamUid);
      if (gen !== teamFetchGenRef.current) return;
      if (res.success) {
        setTeamDetails(res.data.settings);
        setTeamLoader(true)
      }
    } catch (error) {
      console.error('Error fetching team details:', error);
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      if (selectedClient) {
        try {
          const res = await getTeamByClient(selectedClient);
          const sortedTeams = [...(res?.data || [])].sort((a: any, b: any) =>
            (a.name || "").trim().localeCompare((b.name || "").trim())
          );
          const teamsWithAll = [{ uid: 'all', name: 'All' }, ...sortedTeams];
          setTeamOptions(teamsWithAll);
          setSelectedTeam('all')
        } catch {
          setTeamOptions([]);
          setSelectedTeam("");
        }
      } else {
        setTeamOptions([]);
        setSelectedTeam("");
      }
    };
    fetchTeams();
    // viewMode intentionally excluded — which teams belong to a client doesn't
    // depend on table vs. kanban view, and including it caused a duplicate
    // getTeamByClient call whenever the saved viewMode was restored from
    // localStorage shortly after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  return (
    <>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}> */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" fontWeight={600} sx={{textTransform:"uppercase"}}>
                  Missed Calls & Unread Chats
                </Typography>
                <Tooltip title="This sections displays missed calls and unread chats across every process.">
                  <InfoOutlinedIcon color="action" sx={{ fontSize: 18,mb:'5px' }} />
                </Tooltip>
              </Box>
              <Tooltip title="Refresh missed calls & unread chats">
                <IconButton
                  onClick={() => handlePendingCommunicationRefresh()}
                  // disabled={loading}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    marginBottom:'5px',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    },
                    '&:disabled': {
                      bgcolor: 'grey.300'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip> */}
            {/* </Box> */}
        {/* Main Card */}
        {/* <MainCard
          sx={{
            flex: 1,
            borderRadius: 1,
            boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            // minHeight: "20vh",
            mb:1,
            '& .MuiCardContent-root': {
              p: 2,
            },
            "& .MuiCardContent-root:last-child": {
               pb: '14px', 
          }
          }}
        >
          {/* Top Section: Client & Team Selection + Buttons */}
          

          {/* Pending Communication Section */}
          {/* <Box sx={{ mb: 1 }} > */}
            
            {/* <PendingCommunication
              ref={pendingCommunicationRef}
              selectedClient={selectedClient}
              selectedTeam={filters.selectedTeamTable}
              getAllPendingCommunication={getAllPendingCommunication}
            /> */}
          {/* </Box> */}
     {/* </MainCard> */}
     
     <MainCard
          contentSX={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 1,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            '& .MuiCardContent-root': {
              p: '6px',
            },

          }}>

          {/* Current Transactions Section */}
            <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: { xs: "stretch", lg: "center" },
              justifyContent: "space-between",
              mb: '6px',
              gap: 2,
              paddingX:'10px'
            }}
          >
            <Typography variant="h4" fontWeight={600} textTransform={'uppercase'}>
              {/* Dashboard */}Current Transactions
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
                my:'6px',
              }}
            >
              {/* Client */}
              <ClientFilter
                clientOptions={clientOptions}
                selectedClient={selectedClient}
                onChange={setSelectedClient}
                size="small"
                sx={{ minWidth: 180 }}
              />

              {/* Team */}
              {/* Team — was always a FormControl; now swaps to pill when a specific process is active */}
              {filters.selectedTeamTable && filters.selectedTeamTable !== 'all' ? (
                <ActiveFilterPill
                  label="Process"
                  value={teamOptions.find((t: any) => t.uid === filters.selectedTeamTable)?.name ?? filters.selectedTeamTable}
                  onRemove={() => handleFilterChange('selectedTeamTable', 'all')}
                />
              ) : (
                <FormControl size="small" sx={{ minWidth: 180 }} disabled={!selectedClient}>
                  <InputLabel id="team-select-label">Processes</InputLabel>
                  <Select
                    labelId="team-select-label"
                    value={filters.selectedTeamTable}
                    onChange={(e) => handleFilterChange("selectedTeamTable", e.target.value)}
                  >
                    {teamOptions.map((team: any) => (
                      <MenuItem key={team.uid} value={team.uid}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Import */}
              <PermissionWrapper
                permission={PERMISSIONS.BULK_IMPORT_TRANSACTIONS}
                disableInsteadOfHide
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileUploadIcon />}
                  disabled={
                    (!filters.selectedTeamTable || filters.selectedTeamTable === 'all') ||
                    teamDetails?.addTransaction === false
                  }
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import Transactions
                </Button>
              </PermissionWrapper>

              {/* Add Transaction */}
              <PermissionWrapper
                permission={PERMISSIONS.EDIT_TRANSACTION}
                disableInsteadOfHide
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    (!filters.selectedTeamTable || filters.selectedTeamTable === 'all') ||
                    teamDetails?.addTransaction === false
                  }
                  onClick={() => navigate(`/transactions/details/${filters.selectedTeamTable}`)}
                >
                  Add Transaction
                </Button>
              </PermissionWrapper>

              {/* Export Button */}
              <PermissionWrapper
                permission={PERMISSIONS.EXPORT_TRANSACTION}
                disableInsteadOfHide
              >
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={
                    !selectedClient ||
                    (!filters.selectedTeamTable || filters.selectedTeamTable === 'all')
                  }
                  onClick={() => setExportDialogOpen(true)}
                  startIcon={<FileDownloadIcon />}
                >
                  Export
                </Button>
              </PermissionWrapper>
            </Box>
          </Box>

            {/* Filters + Search */}
            <Box
              sx={{
                mb: 1,
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#fff',
                overflow: 'visible',
              }}
            >
              {/* ── Primary filter row ── */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: '10px',
                  py: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {/* ── Date Type ── */}
                {filters.dateType !== 'createdDate' ? (
                  <ActiveFilterPill
                    label="Date type"
                    value={
                      ({ createdDate: 'Created date', updatedDate: 'Updated date', deliveryDate: teamDetails?.dueDateLabel || 'Delivery date' } as Record<string, string>)[filters.dateType] ?? filters.dateType
                    }
                    onRemove={() => handleFilterChange('dateType', 'createdDate')}
                  />
                ) : (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="date-type-label" sx={{ fontSize: '0.78rem' }}>Date Type</InputLabel>
                    <Select
                      labelId="date-type-label"
                      value={filters.dateType}
                      onChange={(e) => handleFilterChange('dateType', e.target.value)}
                      sx={{
                        fontSize: '0.78rem',
                        borderRadius: '8px',
                        backgroundColor: '#F9FAFB',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                      }}
                    >
                      <MenuItem value="updatedDate">Updated Date</MenuItem>
                      <MenuItem value="createdDate">Created Date</MenuItem>
                      <MenuItem value="deliveryDate">{teamLoader ? teamDetails?.dueDateLabel || 'Delivery Date' : 'Delivery Date'}</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* ── Sort by transaction key (kanban only) ──
                    Off by default → cards keep pure date order. When on, the
                    backend appends `key` as a secondary sort within each date. */}
                {viewMode === "card" && (
                  <Tooltip title="Within each date, also sort cards by transaction key (A→Z, 0→9)">
                    <ToggleButton
                      value="sortByKey"
                      size="small"
                      color="secondary"
                      selected={!!filters.sortByKey}
                      onChange={() => handleFilterChange('sortByKey', !filters.sortByKey)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.78rem',
                        borderRadius: '8px',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        px: 1.2,
                        gap: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                          borderColor: 'secondary.main',
                          color: 'secondary.main',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
                          },
                        },
                      }}
                    >
                      <SortByAlphaIcon fontSize="small" />
                      Sort by key
                    </ToggleButton>
                  </Tooltip>
                )}

                {/* ── Divider ── */}
                <Box sx={{ width: '1px', height: 24, backgroundColor: '#E5E7EB', mx: '2px', flexShrink: 0 }} />

                {/* ── Date Range ── */}
                {(() => {
                  const defaultStart = format(addDays(new Date(), -20), 'dd/MM/yyyy');
                  const defaultEnd   = format(addDays(new Date(),  20), 'dd/MM/yyyy');
                  const isDefault = dateRange.startDate === defaultStart && dateRange.endDate === defaultEnd;

                  if (isDefault) {
                    return (
                      <TextField
                        label="Date Range ±20 days from today"
                        value={`${dateRange.startDate} – ${dateRange.endDate}`}
                        size="small"
                        inputProps={{ readOnly: true }}
                        onClick={toggle}
                        sx={{
                          minWidth: 230,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            bgcolor: '#fff',
                            cursor: 'pointer',
                          },
                          '& .MuiOutlinedInput-input': { cursor: 'pointer', padding: '10px 12px' },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    );
                  }

                  return (
                    <ActiveFilterPill
                      label="Date Range"
                      value={`${dateRange.startDate} – ${dateRange.endDate}`}
                      onRemove={() => setDateRange({
                        startDate: format(addDays(new Date(), -20), 'dd/MM/yyyy'),
                        endDate:   format(addDays(new Date(),  20), 'dd/MM/yyyy'),
                      })}
                      onClick={toggle}
                    />
                  );
                })()}

                {openRange && (
                  <Box sx={{ position: 'absolute', zIndex: 1300, mt: 25 }}>
                    <DateRangePickerWrapper
                      open={openRange}
                      toggle={toggle}
                      onChange={handleDateRangeChange}
                      initialDateRange={{
                        startDate: typeof dateRange.startDate === 'string'
                          ? parse(dateRange.startDate, 'dd/MM/yyyy', new Date())
                          : dateRange.startDate,
                        endDate: typeof dateRange.endDate === 'string'
                          ? parse(dateRange.endDate, 'dd/MM/yyyy', new Date())
                          : dateRange.endDate,
                      }}
                    />
                  </Box>
                )}

                {/* ── Divider ── */}
                <Box sx={{ width: '1px', height: 24, backgroundColor: '#E5E7EB', mx: '2px', flexShrink: 0 }} />

                {/* ── Alert & Notifications toggle ──
                    priorityOnly is only implemented server-side for the kanban
                    endpoint (getTransactionsKanban) — the list endpoint
                    (getTransactionsWithFilter) doesn't support it, so this
                    toggle is disabled outside card view instead of silently
                    doing nothing. */}
                <Tooltip title={viewMode === "card" ? '' : 'Alerts & Notifications filtering is only available in Kanban view'}>
                  <Box
                    onClick={() => { if (viewMode === "card") setShowPriorityOnly(prev => !prev); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      px: '2px',
                      py: '2px',
                      borderRadius: '8px',
                      border: `1px solid ${showPriorityOnly ? theme.palette.secondary.dark : '#E5E7EB'}`,
                      backgroundColor: showPriorityOnly ? `${theme.palette.secondary.dark}12` : '#F9FAFB',
                      cursor: viewMode === "card" ? 'pointer' : 'not-allowed',
                      opacity: viewMode === "card" ? 1 : 0.5,
                      transition: 'all 0.15s ease',
                      userSelect: 'none',
                      '&:hover': viewMode === "card" ? { borderColor: theme.palette.secondary.dark, backgroundColor: `${theme.palette.secondary.dark}08` } : {},
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: showPriorityOnly ? theme.palette.secondary.dark : '#D1D5DB',
                        transition: 'background-color 0.15s',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: showPriorityOnly ? 600 : 500,
                        color: showPriorityOnly ? theme.palette.secondary.dark : '#6B7280',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Alerts & Notifications
                    </Typography>
                    <Switch
                      checked={showPriorityOnly}
                      disabled={viewMode !== "card"}
                      onChange={(e) => { e.stopPropagation(); setShowPriorityOnly(e.target.checked); }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        p: 0,
                        width: 28,
                        height: 16,
                        '& .MuiSwitch-switchBase': {
                          p: '2px',
                          '&.Mui-checked': {
                            transform: 'translateX(12px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': { backgroundColor: theme.palette.secondary.dark, opacity: 1 },
                          },
                        },
                        '& .MuiSwitch-thumb': { width: 12, height: 12, boxShadow: 'none' },
                        '& .MuiSwitch-track': { borderRadius: 8, backgroundColor: '#D1D5DB', opacity: 1 },
                      }}
                    />
                  </Box>
                </Tooltip>

                {/* ── Divider ── */}
                <Box sx={{ width: '1px', height: 24, backgroundColor: '#E5E7EB', mx: '2px', flexShrink: 0 }} />

                {/* ── More Filters button ── */}
                <Box
                  onClick={() => {
                    setShowAdvancedFilters(prev => {
                      const next = !prev;
                      if (!next) {
                        handleFilterChange('reportingStatus', '');
                        handleFilterChange('status', '');
                      }
                      return next;
                    });
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    px: '10px',
                    py: '5px',
                    borderRadius: '8px',
                    border: `1px solid ${(filters.reportingStatus || filters.status) ? theme.palette.primary.main : (showAdvancedFilters ? '#9CA3AF' : '#E5E7EB')}`,
                    backgroundColor: (filters.reportingStatus || filters.status)
                      ? `${theme.palette.primary.main}10`
                      : showAdvancedFilters ? '#F3F4F6' : '#F9FAFB',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                    '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F3F4F6' },
                  }}
                >
                  <TuneIcon
                    sx={{
                      fontSize: 18,
                      color: (filters.reportingStatus || filters.status) ? theme.palette.primary.main : '#6B7280',
                    }}
                  />
                  {/* Active count badge */}
                  {(filters.reportingStatus || filters.status) && (
                    <Box
                      sx={{
                        minWidth: 16,
                        height: 16,
                        px: '4px',
                        borderRadius: '10px',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                        {[filters.reportingStatus, filters.status].filter(Boolean).length}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 3.5L5 6.5L8 3.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>
                </Box>

                {/* ── Reset ── */}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearAllFilters}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'none',
                    px: '8px',
                    py: '5px',
                    borderRadius: '8px',
                    minWidth: 0,
                    '&:hover': { backgroundColor: '#FEF2F2', color: '#EF4444' },
                    transition: 'all 0.15s ease',
                  }}
                >
                 Clear Filters
                </Button>

                {/* ── Search — pushed to right ── */}
                <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Search target. Kanban only: the list endpoint ignores
                      searchMode, so offering it there would do nothing. */}
                  {viewMode === "card" && (
                    <Tooltip title={hasProcess ? "" : "Select a process to search by key or field"}>
                      <FormControl size="small" sx={{ minWidth: 110, maxWidth: 170, flexShrink: 0 }} disabled={!hasProcess}>
                        <Select
                          value={searchMode}
                          disabled={!hasProcess}
                          onChange={(e) => applySearchMode(e.target.value as SearchMode)}
                          sx={{
                            fontSize: '0.78rem',
                            borderRadius: '8px',
                            backgroundColor: '#F9FAFB',
                            '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                          }}
                        >
                          <MenuItem value="key" sx={{ fontSize: "0.78rem" }}>{keySearchLabel}</MenuItem>
                          <MenuItem value="field" sx={{ fontSize: "0.78rem" }}>Fields</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>
                  )}

                  {viewMode === "card" && searchMode === "field" && (
                    <Tooltip title={searchableFields.length === 0 ? "Select a process to load its fields" : ""}>
                      <FormControl size="small" sx={{ width: 170, flexShrink: 0 }}>
                        <Select
                          displayEmpty
                          value={searchField}
                          disabled={searchableFields.length === 0}
                          onChange={(e) => applySearchField(e.target.value as string)}
                          renderValue={(selected) =>
                            selected
                              ? (searchableFields.find((f) => f.uid === selected)?.question || String(selected))
                              : "Select field"
                          }
                          sx={{
                            fontSize: '0.78rem',
                            borderRadius: '8px',
                            backgroundColor: '#F9FAFB',
                            '& .MuiSelect-select': { color: searchField ? 'inherit' : '#9CA3AF' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                          }}
                          onClose={() => setFieldFilter("")}
                          // autoFocus off so the menu does not steal focus from
                          // the filter box below the moment it opens.
                          MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
                        >
                          {/* Sticky filter. ListSubheader keeps it pinned while the
                              list scrolls; keystrokes are stopped from bubbling so
                              Select's own type-ahead does not hijack them. */}
                          <ListSubheader sx={{ p: 0, lineHeight: 'normal', bgcolor: 'background.paper' }}>
                            <TextField
                              size="small"
                              fullWidth
                              autoFocus
                              placeholder="Search fields..."
                              value={fieldFilter}
                              onChange={(e) => setFieldFilter(e.target.value)}
                              onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                              sx={{
                                p: '8px',
                                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.78rem', backgroundColor: '#F9FAFB' },
                                '& input::placeholder': { color: '#9CA3AF', fontSize: '0.78rem' },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </ListSubheader>

                          {/* Keeps the current pick a valid option while the filter
                              hides it, so MUI does not warn about an out-of-range
                              value and renderValue still shows the field's name. */}
                          {searchField && !visibleSearchableFields.some((f) => f.uid === searchField) && (
                            <MenuItem value={searchField} sx={{ display: 'none' }} />
                          )}

                          {visibleSearchableFields.map((field) => (
                            <MenuItem key={field.uid} value={field.uid} sx={{ fontSize: "0.78rem" }}>
                              {field.question}
                              {field.step ? (
                                <Typography component="span" sx={{ ml: 0.5, fontSize: '0.68rem', color: '#9CA3AF' }}>
                                  ({field.step})
                                </Typography>
                              ) : null}
                            </MenuItem>
                          ))}

                          {visibleSearchableFields.length === 0 && (
                            <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No fields match</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  )}

                  <TextField
                    placeholder={searchMode === "field" ? (isFieldSearchReady ? "Search value..." : "Pick a field first") : "Search..."}
                    variant="outlined"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    size="small"
                    sx={{
                      width: 180,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        backgroundColor: '#F9FAFB',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                      },
                      '& input::placeholder': { color: '#9CA3AF', fontSize: '0.78rem' },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton edge="end" size="small" onClick={() => { setSearch(''); setDebouncedSearch(''); }} sx={{ p: '2px' }}>
                            <ClearIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>
              </Box>

              {/* ── Expanded "More Filters" panel ── */}
              {showAdvancedFilters && (
                <Box
                  sx={{
                    borderTop: '1px solid #F3F4F6',
                    px: '10px',
                    py: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    backgroundColor: '#FAFAFA',
                    borderRadius: '0 0 12px 12px',
                    animation: 'slideDown 0.15s ease',
                    '@keyframes slideDown': {
                      from: { opacity: 0, transform: 'translateY(-6px)' },
                      to:   { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mr: '4px' }}>
                    Advanced
                  </Typography>

                  {/* Reporting Status */}
                  {filters.reportingStatus ? (
                    <ActiveFilterPill
                      label="Status"
                      value={Array.isArray(nodeStatus) ? (nodeStatus.find((s: any) => s._id === filters.reportingStatus)?.name ?? filters.reportingStatus) : filters.reportingStatus}
                      onRemove={() => handleFilterChange('reportingStatus', '')}
                    />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 170 }} disabled={filters.selectedTeamTable === 'all'}>
                      <InputLabel sx={{ fontSize: '0.78rem' }}>Reporting Status</InputLabel>
                      <Select
                        label="Reporting Status"
                        value={filters.reportingStatus}
                        onChange={(e) => handleFilterChange('reportingStatus', e.target.value)}
                        sx={{
                          fontSize: '0.78rem',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                        }}
                      >
                        {Array.isArray(nodeStatus) && nodeStatus.map((step: any, idx: any) => (
                          <MenuItem key={step.uid + idx} value={step._id}>{step.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Activity Status */}
                  {filters.status ? (
                    <ActiveFilterPill
                      label="Activity"
                      value={filters.status}
                      onRemove={() => handleFilterChange('status', '')}
                    />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 160 }} disabled={filters.selectedTeamTable === 'all'}>
                      <InputLabel sx={{ fontSize: '0.78rem' }}>Activity Status</InputLabel>
                      <Select
                        label="Activity Status"
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        sx={{
                          fontSize: '0.78rem',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                        }}
                      >
                        {Array.isArray(fieldOptions) && fieldOptions.map((step, idx) => (
                          <MenuItem key={step.title + idx} value={`${step.title} Completed`}>
                            {`${step.title} Completed`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </Box>
             <ActiveFiltersBar
              filters={filters}
              dateRange={dateRange}
              teamOptions={teamOptions}
              nodeStatus={nodeStatus}
              fieldOptions={fieldOptions}
              onRemove={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              showPriorityOnly={showPriorityOnly} 
            />
            <Grid container spacing={2} sx={{p:'0'}}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6" color="text.secondary">
                    {/* {selectedClient && filters.selectedTeamTable
                      ? `Showing transactions for ${teamOptions.find(
                        (t) => t.uid === (filters.selectedTeamTable)
                      )?.name || "selected process"
                      }`
                      : ''
                    } */}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1}>
                    {viewMode === "table" && (
                      <>
                        {isDcdClient && (
                          <Tooltip title={hxhActive ? "Back to transactions" : "Show Consolidated HxH"}>
                            <Button
                              size="small"
                              variant={hxhActive ? "contained" : "outlined"}
                              onClick={handleToggleConsolidatedHxH}
                              disabled={hxhLoading}
                              startIcon={hxhLoading ? <CircularProgress size={14} /> : undefined}
                              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px' }}
                            >
                              {hxhActive ? 'Back' : 'Consolidated HxH'}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Refresh transactions">
                          <IconButton
                            onClick={handleRefresh}
                            disabled={loading}
                            size="small"
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.dark' },
                              '&:disabled': { bgcolor: 'grey.300' },
                            }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    {viewMode === "card" && (
                        <>
                          {/* ── Maximized floating toolbar row ── */}
                          {isKanbanMaximized && (
                            <Box
                              sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 44,
                                zIndex: 1301,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 1,
                                px: 2,
                                backgroundColor: 'rgba(249,250,251,0.97)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: '1px solid #E5E7EB',
                                boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                              }}
                            >
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', mr: 'auto' }}>
                                Current Transactions
                              </Typography>

                              {isDcdClient && (
                                <Tooltip title={hxhActive ? "Back to transactions" : "Show Consolidated HxH"}>
                                  <Button
                                    size="small"
                                    variant={hxhActive ? "contained" : "outlined"}
                                    onClick={handleToggleConsolidatedHxH}
                                    disabled={hxhLoading}
                                    startIcon={hxhLoading ? <CircularProgress size={14} /> : undefined}
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px', minWidth: 0, px: '10px', py: '3px' }}
                                  >
                                    {hxhActive ? 'Back' : 'Consolidated HxH'}
                                  </Button>
                                </Tooltip>
                              )}

                              <Tooltip title="Refresh">
                                <IconButton
                                  size="small"
                                  onClick={handleRefresh}
                                  sx={{ borderRadius: '6px', p: '5px', color: '#6B7280', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                >
                                  <RefreshIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Box sx={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '7px', overflow: 'hidden' }}>
                                {(['compact', 'full'] as KanbanViewMode[]).map((mode) => (
                                  <Tooltip key={mode} title={mode === 'compact' ? 'Compact view' : 'Full view'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => setKanbanViewMode(mode)}
                                      sx={{
                                        borderRadius: 0,
                                        p: '5px', px: '7px',
                                        color: kanbanViewMode === mode ? '#fff' : '#6B7280',
                                        backgroundColor: kanbanViewMode === mode ? 'secondary.dark' : '#fff',
                                        '&:hover': { backgroundColor: kanbanViewMode === mode ? 'secondary.dark' : '#F3F4F6' },
                                      }}
                                    >
                                      {mode === 'compact' ? <ViewCompactIcon sx={{ fontSize: 16 }} /> : <ViewComfyIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                  </Tooltip>
                                ))}
                              </Box>

                              <Tooltip title="Exit fullscreen">
                                <IconButton
                                  size="small"
                                  onClick={() => setIsKanbanMaximized(false)}
                                  sx={{ borderRadius: '6px', p: '5px', color: '#6B7280', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                >
                                  <FullscreenExitIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}

                          {/* ── Normal (non-maximized) controls — stay in the existing toolbar row ── */}
                          {true && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isDcdClient && (
                                <Tooltip title={hxhActive ? "Back to transactions" : "Show Consolidated HxH"}>
                                  <Button
                                    size="small"
                                    variant={hxhActive ? "contained" : "outlined"}
                                    onClick={handleToggleConsolidatedHxH}
                                    disabled={hxhLoading}
                                    startIcon={hxhLoading ? <CircularProgress size={14} /> : undefined}
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px', minWidth: 0, px: '10px', py: '3px' }}
                                  >
                                    {hxhActive ? 'Back' : 'Consolidated HxH'}
                                  </Button>
                                </Tooltip>
                              )}

                              <Tooltip title="Refresh Transactions">
                                <IconButton
                                  size="small"
                                  onClick={handleRefresh}
                                  sx={{ borderRadius: '6px', p: '5px', color: '#6B7280', backgroundColor: 'transparent', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                >
                                  <RefreshIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Box sx={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '7px', overflow: 'hidden' }}>
                                {(['compact', 'full'] as KanbanViewMode[]).map((mode) => (
                                  <Tooltip key={mode} title={mode === 'compact' ? 'Compact view' : 'Full view'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => setKanbanViewMode(mode)}
                                      sx={{
                                        borderRadius: 0,
                                        p: '5px', px: '7px',
                                        color: kanbanViewMode === mode ? '#fff' : '#6B7280',
                                        backgroundColor: kanbanViewMode === mode ? 'secondary.dark' : '#fff',
                                        '&:hover': { backgroundColor: kanbanViewMode === mode ? 'secondary.dark' : '#F3F4F6' },
                                      }}
                                    >
                                      {mode === 'compact' ? <ViewCompactIcon sx={{ fontSize: 16 }} /> : <ViewComfyIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                  </Tooltip>
                                ))}
                              </Box>

                              <Tooltip title="Fullscreen">
                                <IconButton
                                  size="small"
                                  onClick={() => setIsKanbanMaximized(true)}
                                  sx={{ borderRadius: '6px', p: '5px', color: '#6B7280', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                >
                                  <FullscreenIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </>
                      )}
                    <ToggleButtonGroup
                      color="primary"
                      value={viewMode}
                      exclusive
                      onChange={handleViewChange}
                      aria-label="view toggle"
                    >
                      <ToggleButton value="table"><TableRowsIcon /></ToggleButton>
                      <ToggleButton value="card"><ViewKanbanIcon /></ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
              </Box>
            </Grid>
          </Grid>
          {hxhActive ? (
            <Box
              sx={{
                minHeight: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {hxhLoading ? (
                <CircularProgress />
              ) : hxhIframeUrl ? (
                <iframe
                  src={hxhIframeUrl}
                  title="Consolidated HxH"
                  frameBorder="0"
                  allowTransparency
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              ) : (
                <Typography color="text.secondary">Unable to load HxH view.</Typography>
              )}
            </Box>
          ) : viewMode === "table" ? (

            <ListViewTransactions loading={loading} transactionData={transactionData} onRefresh={handleRefresh} team={filters.selectedTeamTable} reportingStatus={keyStatusMap}/>
          ) : (
            <KanbanTransactions loading={kanbanIsLoading} transactionData={kanbanData} dateRange={dateRange} reportingStatus={nodeStatus} status={filters.status} search={search} team={filters.selectedTeamTable} reportingStatusMap={keyStatusMap} onRefresh={handleRefresh} onEditModalStateChange={setIsEditModalOpen} showPriorityOnly={showPriorityOnly} kanbanViewMode={kanbanViewMode} isMaximized={isKanbanMaximized}  onMaximizeChange={setIsKanbanMaximized} onLoadMoreColumn={loadMoreKanbanColumn}/>
          )}
        </MainCard>

        {/* Pagination */}
        {viewMode === "table" && !hxhActive && (
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: 'white',
              borderRadius: 2,
              mt: 1
            }}
          >
            <TablePagination
              component="div"
              count={transactionData?.filters?.count || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  px: 3,
                  py: 1.5,
                  minHeight: 52
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: 'text.secondary'
                }
              }}
            />
          </Box>
        )}

        {/* Export Dialog */}
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          teamOptions={teamOptions}
          selectedTeam={filters.selectedTeamTable}
          dateRange={filters.dateRange || ""}
          clientOptions={clientOptions}
          selectedClient={selectedClient || ''}
          currentFilters={{
            search: search,
            reportingStatus: filters.reportingStatus,
            status: filters.status,
            dateType: filters.dateType,
          }}
        />

        {/* Import Dialog */}
        <ImportTransactionsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          teamOptions={teamOptions}
          selectedTeam={filters.selectedTeamTable}
          onImported={handleRefresh}
        />

        <Snackbar
          open={notification.open}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            void handleRealtimeDismiss();
          }}
          sx={(theme) => ({
            '& .MuiPaper-root': {
              borderRadius: theme.shape.borderRadius * 2
            }
          })}
        >
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            variant="outlined"
            sx={(theme) => ({
              alignItems: 'center',
              borderRadius: theme.shape.borderRadius * 2,
              textTransform: 'capitalize',
              borderColor: alpha(theme.palette.info.main, 0.35),
              bgcolor: alpha(theme.palette.info.main, 0.08),
              color: theme.palette.text.primary,
              boxShadow: theme.shadows[2],
              backdropFilter: 'blur(8px)',
              '& .MuiAlert-icon': {
                color: theme.palette.info.main
              },
              '& .MuiAlert-message': {
                paddingTop: theme.spacing(0.25),
                paddingBottom: theme.spacing(0.25)
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                paddingTop: 0
              }
            })}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="info"
                  size="small"
                  variant="text"
                  onClick={() => void handleRealtimeRefreshNow()}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Refresh now
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="text"
                  onClick={() => void handleRealtimeDismiss()}
                  sx={{ textTransform: 'none' }}
                >
                  Dismiss
                </Button>
              </Box>
            }
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default NewDashboard;